import React from 'react';
import '../../styles/tables.css';

export interface Column<T = any> {
  field: string;
  header: string;
  renderHeader?: () => React.ReactNode;
  render?: (row: T) => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface BaseTableProps<T extends { id?: number | string }> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  onEdit?: (item: T) => void;
  onDelete?: (id: number | string) => void;
  emptyMessage?: string;
  filters?: React.ReactNode;
  className?: string;
  rowKey?: (row: T) => string | number;
  showActions?: boolean;
}

function BaseTable<T extends { id?: number | string }>({
  columns,
  data,
  loading = false,
  onEdit,
  onDelete,
  emptyMessage = 'No hay datos disponibles',
  filters,
  className = 'base-table',
  rowKey,
  showActions = true,
}: BaseTableProps<T>) {
  const getRowKey = (row: T, index: number): string | number => {
    if (rowKey) return rowKey(row);
    return row.id ?? index;
  };

  if (loading) {
    return (
      <div className="table-container">
        {filters && <div style={{ padding: '24px' }}>{filters}</div>}
        <div className="table-loading">
          <div className="table-spinner"></div>
          <span>Cargando datos...</span>
        </div>
      </div>
    );
  }

  const hasActions = showActions && (onEdit || onDelete);

  return (
    <div className="table-container">
      {filters && <div style={{ padding: '24px' }}>{filters}</div>}
      
      <table className={className}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.field}
                style={{
                  width: col.width,
                  textAlign: col.align || 'left',
                }}
              >
                {col.renderHeader ? col.renderHeader() : col.header}
              </th>
            ))}
            {hasActions && <th style={{ textAlign: 'right' }}>Acciones</th>}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (hasActions ? 1 : 0)}
                className="table-empty"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr key={getRowKey(row, index)}>
                {columns.map((col) => (
                  <td
                    key={col.field}
                    data-label={col.header}
                    style={{ textAlign: col.align || 'left' }}
                  >
                    {col.render ? col.render(row) : ((row as any)[col.field] ?? '—')}
                  </td>
                ))}
                {hasActions && (
                  <td className="table-actions" data-label="">
                    {onEdit && (
                      <button
                        className="btn-edit"
                        onClick={() => onEdit(row)}
                        title="Editar"
                      >
                        Editar
                      </button>
                    )}
                    {onDelete && row.id && (
                      <button
                        className="btn-delete"
                        onClick={() => onDelete(row.id!)}
                        title="Eliminar"
                      >
                        Eliminar
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default BaseTable;
